import { useState } from "react";
import { AlertCircle, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  PHONE_MAX_CHARS,
  formatPhoneAsTyped,
  stripDigitsForName,
  validateContactForm,
} from "@/lib/validators";

export const POPIA_CONSENT =
  "I'm happy for T-TITE to use these details to prepare and discuss my quote. I understand my " +
  "information is stored securely and will never be sold or shared with third parties.";

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  best_time: "",
  message: "",
  consent: false,
  website_url: "",
};

const Field = ({ id, label, hint, error, required, children }) => (
  <div className="flex flex-col">
    <Label htmlFor={id} className="text-sm font-semibold text-foreground">
      {label}
      {required ? (
        <span className="ml-1 text-destructive" aria-hidden="true">
          *
        </span>
      ) : (
        <span className="ml-1.5 text-xs font-normal text-muted-foreground">(optional)</span>
      )}
    </Label>
    {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    <div className="mt-2">{children}</div>
    {error ? (
      <p
        role="alert"
        data-testid={`contact-error-${id}`}
        className="mt-1.5 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
      >
        <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {error}
      </p>
    ) : null}
  </div>
);

/**
 * The required contact gate, and (with `variant="fallback"`) the
 * "not catered for" form. Same fields, same validation rules, same honeypot.
 */
export const ContactForm = ({
  variant = "gate",
  busy = false,
  serverErrors = null,
  onSubmit,
  submitLabel,
  idPrefix = "contact",
}) => {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const isFallback = variant === "fallback";

  const merged = { ...errors, ...(serverErrors || {}) };

  const setField = (key) => (event) => {
    const value = event?.target?.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [key]: value }));
    if (merged[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  /** A person's name takes letters only — digits are dropped as they're typed. */
  const setName = (event) => {
    const value = stripDigitsForName(event.target.value);
    setValues((prev) => ({ ...prev, full_name: value }));
    if (merged.full_name) setErrors((prev) => ({ ...prev, full_name: undefined }));
  };

  /** Phone spaces itself out as "081 788 2993" and stops at 12 characters. */
  const setPhone = (event) => {
    const value = formatPhoneAsTyped(event.target.value);
    setValues((prev) => ({ ...prev, phone: value }));
    if (merged.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const found = validateContactForm(values, { requireConsent: true });
    setErrors(found);
    if (Object.keys(found).length) {
      const firstKey = Object.keys(found)[0];
      document.getElementById(`${idPrefix}-${firstKey}`)?.focus();
      return;
    }
    onSubmit(values);
  };

  const errorCount = Object.values(merged).filter(Boolean).length;

  return (
    <form
      data-testid={isFallback ? "fallback-contact-form" : "contact-gate-form"}
      onSubmit={handleSubmit}
      noValidate
      className="space-y-5"
    >
      {errorCount > 0 ? (
        <div
          role="alert"
          aria-live="assertive"
          data-testid={`${idPrefix}-error-summary`}
          className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm font-medium text-destructive"
        >
          Please fix {errorCount} {errorCount === 1 ? "field" : "fields"} below and try again.
        </div>
      ) : null}

      {/* Honeypot — hidden from people, irresistible to bots. */}
      <div className="tt-honeypot" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website_url`}>
          If you are human, leave this field blank
        </label>
        <input
          id={`${idPrefix}-website_url`}
          data-testid={isFallback ? "fallback-honeypot" : "contact-gate-honeypot"}
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          value={values.website_url}
          onChange={setField("website_url")}
        />
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field
          id={`${idPrefix}-full_name`}
          label="Full name"
          required
          error={merged.full_name}
        >
          <Input
            id={`${idPrefix}-full_name`}
            data-testid={`${idPrefix}-full-name-input`}
            value={values.full_name}
            onChange={setName}
            autoComplete="name"
            inputMode="text"
            maxLength={120}
            placeholder="Thabo Mokoena"
            aria-invalid={merged.full_name ? "true" : "false"}
            className={`h-12 ${merged.full_name ? "border-destructive" : ""}`}
          />
        </Field>

        <Field
          id={`${idPrefix}-company`}
          label="Company / business name"
          required
          error={merged.company}
        >
          <Input
            id={`${idPrefix}-company`}
            data-testid={`${idPrefix}-company-input`}
            value={values.company}
            onChange={setField("company")}
            autoComplete="organization"
            placeholder="Mokoena Trading (Pty) Ltd"
            aria-invalid={merged.company ? "true" : "false"}
            className={`h-12 ${merged.company ? "border-destructive" : ""}`}
          />
        </Field>

        <Field id={`${idPrefix}-email`} label="Email address" required error={merged.email}>
          <Input
            id={`${idPrefix}-email`}
            data-testid={`${idPrefix}-email-input`}
            type="email"
            value={values.email}
            onChange={setField("email")}
            autoComplete="email"
            placeholder="you@yourbusiness.co.za"
            aria-invalid={merged.email ? "true" : "false"}
            className={`h-12 ${merged.email ? "border-destructive" : ""}`}
          />
        </Field>

        <Field
          id={`${idPrefix}-phone`}
          label="Phone number"
          required
          error={merged.phone}
        >
          <Input
            id={`${idPrefix}-phone`}
            data-testid={`${idPrefix}-phone-input`}
            type="tel"
            value={values.phone}
            onChange={setPhone}
            autoComplete="tel"
            inputMode="tel"
            maxLength={PHONE_MAX_CHARS}
            placeholder="081 788 2993"
            aria-invalid={merged.phone ? "true" : "false"}
            className={`h-12 ${merged.phone ? "border-destructive" : ""}`}
          />
        </Field>
      </div>

      {isFallback ? (
        <Field
          id={`${idPrefix}-message`}
          label="Tell us about your business"
          hint="Anything that helps us scope this properly — how many years you're behind, what you need, and by when."
          error={merged.message}
        >
          <Textarea
            id={`${idPrefix}-message`}
            data-testid={`${idPrefix}-message-input`}
            value={values.message}
            onChange={setField("message")}
            rows={4}
            placeholder="We're four years behind on our financial statements and need to get compliant with SARS…"
          />
        </Field>
      ) : (
        <Field
          id={`${idPrefix}-best_time`}
          label="Best time to contact you"
          error={merged.best_time}
        >
          <Input
            id={`${idPrefix}-best_time`}
            data-testid={`${idPrefix}-best-time-input`}
            value={values.best_time}
            onChange={setField("best_time")}
            placeholder="Weekday mornings"
            className="h-12"
          />
        </Field>
      )}

      <div
        className={`rounded-lg border p-4 ${
          merged.consent
            ? "border-destructive bg-destructive/5"
            : "border-border bg-[color:var(--tt-surface-2)]"
        }`}
      >
        <div className="flex items-start gap-3">
          <Checkbox
            id={`${idPrefix}-consent`}
            data-testid={isFallback ? "fallback-consent-checkbox" : "contact-gate-consent-checkbox"}
            checked={values.consent}
            onCheckedChange={(checked) => {
              setValues((prev) => ({ ...prev, consent: checked === true }));
              if (merged.consent) setErrors((prev) => ({ ...prev, consent: undefined }));
            }}
            className="mt-0.5 h-5 w-5 shrink-0 rounded-[3px] border-2 bg-white [&_svg]:h-4 [&_svg]:w-4"
            aria-invalid={merged.consent ? "true" : "false"}
          />
          <Label
            htmlFor={`${idPrefix}-consent`}
            className="cursor-pointer text-[13px] font-normal leading-relaxed text-muted-foreground"
          >
            {POPIA_CONSENT}
            <span className="ml-1 font-semibold text-destructive" aria-hidden="true">
              *
            </span>
            <span className="sr-only"> (required)</span>
          </Label>
        </div>
        {merged.consent ? (
          <p
            role="alert"
            data-testid={`contact-error-${idPrefix}-consent`}
            className="mt-2 flex items-start gap-1.5 text-[13px] font-medium text-destructive"
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {merged.consent}
          </p>
        ) : null}
      </div>

      <Button
        type="submit"
        disabled={busy}
        data-testid={isFallback ? "fallback-submit-button" : "contact-gate-submit-button"}
        className="h-12 w-full text-base font-bold sm:w-auto sm:min-w-[260px]"
      >
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
        {submitLabel ||
          (isFallback ? "Send this to the T-TITE team" : "Show me my price")}
      </Button>

      <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        {isFallback ? (
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
        ) : (
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
        )}
        <span>
          Stored securely in line with South Africa's Protection of Personal Information Act
          (POPIA). Used only to prepare and discuss your quote — never sold, never shared.
        </span>
      </p>
    </form>
  );
};
